import { type JSX, Show } from "solid-js";
import { useLocation } from "@solidjs/router";
import { useUserContext } from "../components/UserContextProvider";
import { useAdminAccess } from "./useAdminAccess";
import { useTranslation } from "../hooks/useTranslation";
import { EmptyState } from "../components/EmptyState";
import Footer from "../components/Footer";
import Login from "../pages/Login";
import AdminLayout from "./AdminLayout";
import { AdminNavbar } from "./AdminNavbar";

// Login.tsx reads its post-login destination from ?redirectUri in the URL on mount,
// falling back to /me (a main-app-only route) when it's absent. AdminGate renders
// Login inline rather than navigating there, so the URL never picks up that param on
// its own — stamp it in before Login mounts, or a successful admin login lands on /me,
// a page that doesn't exist here.
function LoginWithRedirect() {
    const location = useLocation();
    const url = new URL(window.location.href);
    url.searchParams.set('redirectUri', location.pathname);
    window.history.replaceState(null, '', url);

    return (
        <div style={{ display: 'flex', 'flex-direction': 'column', width: '100%', 'min-height': 'calc(100vh - 56px)' }}>
            <div style={{ flex: '1 0 auto' }}><Login /></div>
            <Footer />
        </div>
    );
}

export function AdminGate(props: { children: JSX.Element }) {
    const { user, sessionChecked } = useUserContext();
    const { hasAdminAccess } = useAdminAccess();
    const { t } = useTranslation();

    return (
        <AdminLayout>
            <Show when={sessionChecked()} fallback={
                <div style={{ display: 'flex', 'justify-content': 'center', padding: 'var(--spacing-4xl)' }}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            }>
                <Show when={user().loggedin} fallback={<LoginWithRedirect />}>
                    <Show when={hasAdminAccess()} fallback={
                        <div style={{ padding: 'var(--spacing-2xl)', "max-width": '600px', margin: '0 auto' }}>
                            <EmptyState
                                icon="fa-user-lock"
                                title={t('ADMIN_ACCESS_DENIED_TITLE')}
                                hint={t('ADMIN_ACCESS_DENIED_HINT')}
                            />
                        </div>
                    }>
                        <AdminNavbar>{props.children}</AdminNavbar>
                    </Show>
                </Show>
            </Show>
        </AdminLayout>
    );
}
